import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import { useQuery } from "@tanstack/react-query"
import DateDisplay from "design-system/molecules/DateDisplay"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { ChevronRight, RefreshCw } from "lucide-react"
import { Fragment, useState } from "react"
import { getBackofficeUnansweredQuestionTopicsQuery } from "modules/backoffice/lib/knowledge/getBackofficeUnansweredQuestionTopicsQuery"
import { useKnowledgeTableSort } from "modules/backoffice/lib/knowledge/useKnowledgeTableSort"
import { useUpdateBackofficeKnowledgeCompanyMutation } from "modules/backoffice/lib/knowledge/useUpdateBackofficeKnowledgeCompanyMutation"
import { useTranslation } from "react-i18next"
import { BackofficeKnowledgeOverviewResponseDataSchema } from "schema"
import { z } from "zod"
import { BackofficeCompanyUnansweredTopics } from "./BackofficeCompanyUnansweredTopics"
import { BackofficeKnowledgeComparisonCells } from "./BackofficeKnowledgeComparisonCells"
import { BackofficeKnowledgeSection } from "./BackofficeKnowledgeSection"

type Companies = z.infer<typeof BackofficeKnowledgeOverviewResponseDataSchema>["companies"]
type Props = {
  data: Companies | undefined
  isPending: boolean
  isRecalculateDisabled: boolean
  onRecalculate: () => void
}
const COLUMN_COUNT = 8

export function BackofficeKnowledgeCompaniesTable({
  data,
  isPending,
  isRecalculateDisabled,
  onRecalculate,
}: Props) {
  const { t } = useTranslation()
  const [expandedCompanyId, setExpandedCompanyId] = useState<string>()
  const topicsQuery = useQuery(getBackofficeUnansweredQuestionTopicsQuery(undefined))
  const updateCompany = useUpdateBackofficeKnowledgeCompanyMutation()
  const generatedAt = topicsQuery.data?.reduce<string | null>(
    (latest, company) =>
      company.generatedAt && (!latest || company.generatedAt > latest)
        ? company.generatedAt
        : latest,
    null,
  )

  const { sortedData, sortable } = useKnowledgeTableSort(data, {
    companyName: (row) => row.companyName,
    slackNotifications: (row) => Number(row.shouldNotifyKnowledgeGapsOnSlack),
    ticketIntegration: (row) => row.ticketIntegrations.join(", "),
    questionCount: (row) => row.questionCount,
    answeredRate: (row) => row.answeredRate,
    score: (row) => row.score,
    knowledgePendingCount: (row) => row.knowledgePendingCount,
    evolution: (row) => row.answeredRateChangePercentagePoints,
  })

  function toggle(companyId: string) {
    setExpandedCompanyId((current) => (current === companyId ? undefined : companyId))
  }

  function renderContent() {
    if (isPending || data === undefined)
      return <TableSkeleton colSpans={Array(COLUMN_COUNT).fill(1)} />
    if (!data.length)
      return (
        <TableEmptySlate
          colSpan={COLUMN_COUNT}
          message={t("backoffice.knowledge.companies.empty")}
        />
      )

    return sortedData.map((row) => {
      const isExpanded = expandedCompanyId === row.companyId
      return (
        <Fragment key={row.companyId}>
          <TableRow
            onClick={() => toggle(row.companyId)}
            className={cn("cursor-pointer", isExpanded && "bg-muted")}
          >
            <TableCell>
              <button
                type="button"
                aria-expanded={isExpanded}
                className="flex items-center gap-2 text-left font-medium"
                onClick={(event) => {
                  event.stopPropagation()
                  toggle(row.companyId)
                }}
              >
                <ChevronRight
                  className={cn("size-4 shrink-0 transition-transform", isExpanded && "rotate-90")}
                />
                {row.companyName}
              </button>
            </TableCell>
            <TableCell onClick={(event) => event.stopPropagation()}>
              <Checkbox
                aria-label={t("backoffice.knowledge.companies.slackNotificationsLabel", {
                  company: row.companyName,
                })}
                checked={row.shouldNotifyKnowledgeGapsOnSlack}
                disabled={updateCompany.isPending}
                onCheckedChange={(checked) =>
                  updateCompany.mutate({
                    companyId: row.companyId,
                    shouldNotifyKnowledgeGapsOnSlack: checked === true,
                  })
                }
              />
            </TableCell>
            <TableCell className="text-muted-foreground">
              {row.ticketIntegrations.join(", ")}
            </TableCell>
            <BackofficeKnowledgeComparisonCells row={row} />
          </TableRow>
          {isExpanded && (
            <BackofficeCompanyUnansweredTopics
              columnCount={COLUMN_COUNT}
              company={topicsQuery.data?.find((company) => company.companyId === row.companyId)}
              isPending={topicsQuery.isPending}
              isError={topicsQuery.isError}
              onRetry={() => topicsQuery.refetch()}
            />
          )}
        </Fragment>
      )
    })
  }

  return (
    <BackofficeKnowledgeSection
      title={t("backoffice.knowledge.companies.title")}
      description={t("backoffice.knowledge.companies.description")}
      headerAction={
        <div className="ml-auto flex items-center gap-2">
          {generatedAt && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              {t("backoffice.knowledge.dailyTopics.generatedAt")}
              {": "}
              <DateDisplay date={generatedAt} />
            </div>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t("backoffice.knowledge.dailyTopics.recalculate")}
            title={t("backoffice.knowledge.dailyTopics.recalculate")}
            disabled={isRecalculateDisabled}
            onClick={onRecalculate}
          >
            <RefreshCw />
          </Button>
        </div>
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead sortable={sortable("companyName")}>
              {t("backoffice.knowledge.table.company")}
            </TableHead>
            <TableHead
              sortable={sortable("slackNotifications")}
              title={t("backoffice.knowledge.companies.slackNotificationsHelp")}
            >
              {t("backoffice.knowledge.companies.slackNotifications")}
            </TableHead>
            <TableHead sortable={sortable("ticketIntegration")}>
              {t("backoffice.knowledge.companies.ticketIntegration")}
            </TableHead>
            <TableHead align="right" sortable={sortable("questionCount")}>
              {t("backoffice.knowledge.metrics.questionCount")}
            </TableHead>
            <TableHead align="right" sortable={sortable("answeredRate")}>
              {t("backoffice.knowledge.metrics.answeredRate")}
            </TableHead>
            <TableHead
              align="right"
              title={t("backoffice.knowledge.metrics.scoreHelp")}
              sortable={sortable("score")}
            >
              {t("backoffice.knowledge.metrics.score")}
            </TableHead>
            <TableHead align="right" sortable={sortable("knowledgePendingCount")}>
              <Tooltip>
                <TooltipTrigger asChild slick>
                  <span tabIndex={0}>
                    {t("backoffice.knowledge.metrics.knowledgePendingCount")}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {t("backoffice.knowledge.metrics.knowledgePendingHelp")}
                </TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead align="right" sortable={sortable("evolution")}>
              <Tooltip>
                <TooltipTrigger asChild slick>
                  <span tabIndex={0}>{t("backoffice.knowledge.evolution.title")}</span>
                </TooltipTrigger>
                <TooltipContent>{t("backoffice.knowledge.evolution.help")}</TooltipContent>
              </Tooltip>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>{renderContent()}</TableBody>
      </Table>
    </BackofficeKnowledgeSection>
  )
}
